import { useRouter } from "next/router"
import { parseAsInteger, parseAsString, useQueryState } from "nuqs"
import { useCallback } from "react"

import apiClient from "@api/client"
import { useFulfillmentOptions } from "hooks/useFulfillmentOptions"
import { useSetDefaultDeliveryAddress } from "hooks/useSetDefaultDeliveryAddress"
import { useCart } from "providers/cart-provider"

import { useCreateDeliveryAddress } from "./use-create-delivery-address"
import { Address } from "./use-search-address"

type EditDeliveryAddressPreferences = {
  street2?: string
  entryCode?: string
  addressLabel?: string
  deliveryInstructions?: string
}

function editDeliveryAddress(
  deliveryAddressId: number,
  preferences: EditDeliveryAddressPreferences,
) {
  return apiClient.post(`user/delivery-address/${deliveryAddressId}`, {
    preferences: preferences,
  })
}

export function useDelivery() {
  const { cart } = useCart()
  const createDeliveryAddress = useCreateDeliveryAddress()
  const setDefaultDeliveryAddress = useSetDefaultDeliveryAddress({})

  const router = useRouter()
  const { setPendingSelection } = useFulfillmentOptions({})

  // Temporary
  const [tempDeliveryAddress, setTempDeliveryAddress] = useQueryState(
    "tempDeliveryAddress",
    parseAsString,
  )

  const [deliveryAddressEntryCode, setDeliveryAddressEntryCode] = useQueryState(
    "tempDeliveryAddressEntryCode",
    parseAsString,
  )

  const [
    deliveryAddressDeliveryInstructions,
    setDeliveryAddressDeliveryInstructions,
  ] = useQueryState("deliveryAddressDeliveryInstructions", parseAsString)

  const [deliveryAddressUnitNumber, setDeliveryAddressUnitNumber] =
    useQueryState("deliveryAddressUnitNumber", parseAsString)

  const [deliveryAddress, setDeliveryAddress] = useQueryState(
    "deliveryAddress",
    parseAsString,
  )

  // In new address form, we need to store the place id to create the address
  const [deliveryAddressPlaceId, setDeliveryAddressPlaceId] = useQueryState(
    "deliveryAddressPlaceId",
    parseAsString,
  )

  // With saved addresses, we need to store the address id to set as default
  const [deliveryAddressId, setDeliveryAddressId] = useQueryState(
    "deliveryAddressId",
    parseAsInteger,
  )

  const handleSelectDelivery = useCallback(
    async (storeSlug: string) => {
      if (!cart.data) {
        return
      }

      setPendingSelection("delivery")

      try {
        if (deliveryAddressId) {
          await editDeliveryAddress(deliveryAddressId, {
            street2: deliveryAddressUnitNumber ?? undefined,
            entryCode: deliveryAddressEntryCode ?? undefined,
            addressLabel: "",
            deliveryInstructions:
              deliveryAddressDeliveryInstructions ?? undefined,
          })

          return await setDefaultDeliveryAddress.mutateAsync(
            deliveryAddressId,
            {
              onSuccess: async () => {
                setPendingSelection(null)
                await cart.refetch()
                router.push(`/${storeSlug}`)
              },
            },
          )
        }

        if (deliveryAddressPlaceId) {
          const createdDeliveryAddressId =
            await createDeliveryAddress.mutateAsync({
              google_place_id: deliveryAddressPlaceId,
              preferences: {
                street2: deliveryAddressUnitNumber,
                entryCode: deliveryAddressEntryCode,
                addressLabel: "",
                deliveryInstructions: deliveryAddressDeliveryInstructions,
              },
            })

          await setDefaultDeliveryAddress.mutateAsync(createdDeliveryAddressId)
        }
      } catch (error) {
        throw error
      }
    },
    [
      cart,
      createDeliveryAddress,
      deliveryAddressPlaceId,
      setDefaultDeliveryAddress,
      deliveryAddressId,
      router,
      deliveryAddressEntryCode,
      deliveryAddressDeliveryInstructions,
      setPendingSelection,
      deliveryAddressUnitNumber,
    ],
  )

  function handleSelectAddress(address: Address) {
    setTempDeliveryAddress(`${address.main_text}, ${address.secondary_text}`)
    setDeliveryAddressPlaceId(address.place_id)
  }

  return {
    // Temporary
    tempDeliveryAddress,
    setTempDeliveryAddress,
    deliveryAddressEntryCode,
    setDeliveryAddressEntryCode,
    deliveryAddressDeliveryInstructions,
    setDeliveryAddressDeliveryInstructions,
    deliveryAddressUnitNumber,
    setDeliveryAddressUnitNumber,

    deliveryAddress,
    setDeliveryAddress,
    deliveryAddressPlaceId,
    setDeliveryAddressPlaceId,

    deliveryAddressId,
    setDeliveryAddressId,

    handleSelectDelivery,
    handleSelectAddress,
  }
}
